import { ComponentType, ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { FinancialWorkspace } from 'modules/finance/components/FinancialWorkspace'
import { ResponsivePageWithPanel } from 'modules/finance/components/FinancialWorkspace/components/ResponsivePageWithPanel'
import {
  InsightTableGroupBy,
  InsightTableType,
} from 'modules/finance/components/FinancialWorkspace/types/table'

import { AvailabilitiesProvider } from './Providers/Availabilities'
import { CachedDataProvider } from './Providers/CachedDataProvider'
import { CustomFiltersProvider } from './Providers/CustomFilters'
import { DataProvider } from './Providers/DataProvider'
import { ExtraDataProvider } from './Providers/ExtraData'
import { PermissionsProvider } from './Providers/PermissionsProvider'
import { useFilters } from './components/Filters/useFilters'
import { Header } from './components/Header'
import { CurrentPeriodButton } from './components/HistoryPanel/CurrentPeriodButton'
import { useTableProps } from './components/Table/props'
import { EmployeeCostsChart } from './components/chart'
import { TAB_NAME } from './consts'

interface FinancialWorkspaceEmployeeCostsProps {
  authorized?: boolean
}

const FinancialWorkspaceEmployeeCosts: React.FC<FinancialWorkspaceEmployeeCostsProps> = ({
  authorized,
}) => {
  const CustomProvider: ComponentType<{ children: ReactNode }> = ({ children }) => (
    <CachedDataProvider>
      <PermissionsProvider>
        <AvailabilitiesProvider>
          <ExtraDataProvider>
            <CustomFiltersProvider>
              <DataProvider>{children}</DataProvider>
            </CustomFiltersProvider>
          </ExtraDataProvider>
        </AvailabilitiesProvider>
      </PermissionsProvider>
    </CachedDataProvider>
  )

  return (
    <ResponsivePageWithPanel
      authorized={authorized}
      showHeader={false}
      variant="fullscreen"
      panelTitle={i18n.t('finance.financial_workspace.employee_costs.workforce.history')}
      onOpen={() => {
        track(trackingEvents.accounting.newFinancialWorkspace.workforceHistory.opened, {
          source: `${TAB_NAME}`,
        })
      }}
    >
      <FinancialWorkspace
        header={<Header />}
        tableProps={useTableProps()}
        dataProvider={CustomProvider}
        chart={<EmployeeCostsChart />}
        useAdditionalFilters={useFilters}
        defaultGroupBy={InsightTableGroupBy.Total}
        customPeriodComponent={<CurrentPeriodButton />}
        insightTableType={InsightTableType.EmployeeCosts}
      />
      <Outlet context={{ canSeeFinanceWorkspace: authorized }} />
    </ResponsivePageWithPanel>
  )
}

export default FinancialWorkspaceEmployeeCosts
